import React, { useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from './hooks/redux';
import { updateLocation } from './store/slices/authSlice';
import { wsService } from './services/websocket';
import { analyticsTracker } from './services/analytics';
import Layout from './components/Layout';
import Home from './pages/Home';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import PostDetail from './pages/PostDetail';
import Conversations from './pages/Conversations';
import Chat from './pages/Chat';
import Feeds from './pages/Feeds';
import OnlineUsers from './pages/OnlineUsers';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import AdminLocationSetup from './pages/AdminLocationSetup';

const LOCATION_UPDATE_INTERVAL = 30 * 60 * 1000; // 30 minutes

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const location = useLocation();

  // Admin users without a location set should be redirected to location setup
  const needsLocationSetup = isAuthenticated && user?.is_admin && !user?.city;

  const sendLocationUpdate = useCallback(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        dispatch(updateLocation({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }));
      },
      () => {} // silently ignore errors
    );
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      wsService.connect();
      analyticsTracker.start();
      // Update location immediately and every 30 minutes
      sendLocationUpdate();
      const interval = setInterval(sendLocationUpdate, LOCATION_UPDATE_INTERVAL);
      return () => {
        wsService.disconnect();
        analyticsTracker.stop();
        clearInterval(interval);
      };
    } else {
      wsService.disconnect();
      analyticsTracker.stop();
    }
    return () => {
      wsService.disconnect();
    };
  }, [isAuthenticated, sendLocationUpdate]);

  // Track page views on route change
  useEffect(() => {
    if (isAuthenticated) {
      analyticsTracker.trackPageView(location.pathname);
    }
  }, [location.pathname, isAuthenticated]);

  return (
    <Routes>
      <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/" />} />
      <Route path="/register" element={!isAuthenticated ? <Register /> : <Navigate to="/" />} />
      <Route path="/admin/location-setup" element={isAuthenticated ? <AdminLocationSetup /> : <Navigate to="/login" />} />

      {/* Public landing page at "/" for logged-out visitors */}
      {!isAuthenticated && <Route path="/" element={<Landing />} />}

      <Route element={<Layout />}>
        {isAuthenticated && (
          <Route path="/" element={needsLocationSetup ? <Navigate to="/admin/location-setup" /> : <Home />} />
        )}
        <Route path="/profile/:id" element={isAuthenticated ? <Profile /> : <Navigate to="/login" />} />
        <Route path="/post/:id" element={isAuthenticated ? <PostDetail /> : <Navigate to="/login" />} />
        <Route path="/messages" element={isAuthenticated ? <Conversations /> : <Navigate to="/login" />} />
        <Route path="/messages/:id" element={isAuthenticated ? <Chat /> : <Navigate to="/login" />} />
        <Route path="/admin/feeds" element={isAuthenticated ? <Feeds /> : <Navigate to="/login" />} />
        <Route path="/admin/analytics" element={isAuthenticated ? <AnalyticsDashboard /> : <Navigate to="/login" />} />
        <Route path="/admin/online" element={isAuthenticated ? <OnlineUsers /> : <Navigate to="/login" />} />
      </Route>
    </Routes>
  );
};

export default App;
